import { useCallback, useEffect, useState } from 'react';
import {
  InputBase,
  Select as MuiSelect,
  FormControl,
  Box,
  Button,
  Stack,
  Typography,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { Column, Table as TableType, Row } from '@tanstack/react-table';
import { PlusIcon } from '../../../icons';
import { DatePicker, Input, Select } from '../../atomic';
import { useTranslation } from 'react-i18next';

type TableFilterProps<T> = {
  table: TableType<T>;
};

export const TableFilter = <T,>({ table }: TableFilterProps<T>) => {
  const { t } = useTranslation();
  const [selectedField] = useState<string>('');
  const [activeFields, setActiveFields] = useState<string[]>([]);

  useEffect(() => {
    const allFilterableColumnIds = table
      .getAllColumns()
      .filter((column) => column.getCanFilter())
      .map((column) => column.id);
    setActiveFields(allFilterableColumnIds);
  }, [table]);

  const filterColumns = table
    .getAllColumns()
    .filter((column: Column<T>) => column.getCanFilter() && !activeFields.includes(column.id));

  const handleFilterChange = (event: SelectChangeEvent) => {
    const field = event.target.value;
    if (field) {
      setActiveFields((prev) => [...prev, field]);
      table.resetRowSelection();
    }
  };

  const handleRemoveFilter = (fieldToRemove: string) => {
    table.getColumn(fieldToRemove)?.setFilterValue(undefined);
    setActiveFields((oldFields) => oldFields.filter((field) => field !== fieldToRemove));
    table.resetRowSelection();
  };

  const handleTextFilterValueChange = (column: Column<T>) => (value: string) => {
    column?.setFilterValue(value);
    table.resetRowSelection();
  };

  const handleSelectFilterValueChange = (column: Column<T>) => (e: SelectChangeEvent<string>) => {
    column?.setFilterValue(e.target.value);
    table.resetRowSelection();
  };

  const handleBooleanFilterValueChange = (column: Column<T>) => (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    if (value === '') {
      column?.setFilterValue(undefined);
    } else if (value === 'true') {
      column?.setFilterValue(true);
    } else {
      column?.setFilterValue(false);
    }
    table.resetRowSelection();
  };

  const handleRangeFilterValueChange = (column: Column<T>, isMax: boolean) => (value: string) => {
    const [min, max] = (column?.getFilterValue() as [number | string, number | string]) || [
      null,
      null
    ];

    if (isMax) {
      column?.setFilterValue(value ? [min, value] : [min, null]);
    } else {
      column?.setFilterValue(value ? [value, max] : [null, max]);
    }

    table.resetRowSelection();
  };

  return (
    <Stack spacing={1}>
      {activeFields.length > 0 && (
        <Stack spacing={1}>
          {activeFields.map((field: string) => {
            const column = table.getColumn(field);

            if (!column) {
              return null;
            }

            const filterType = column?.columnDef.meta?.filterVariant;

            if (filterType === 'boolean' && !column?.columnDef.filterFn) {
              // Create a filter function that properly handles boolean values
              const booleanFilterFn = (row: Row<T>, columnId: string, filterValue: boolean) => {
                const value = row.getValue(columnId);
                return value === filterValue;
              };

              // Add the resolveFilterValue method to convert strings to booleans
              booleanFilterFn.resolveFilterValue = (filterValue: string) => {
                if (filterValue === 'true') return true;
                if (filterValue === 'false') return false;
                return undefined;
              };
              // Set the filter function on the column
              if (column) {
                column.columnDef.filterFn = booleanFilterFn;
              }
            }

            return (
              <Box
                key={field}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  alignItems: 'center',
                  backgroundColor: 'background.paper',
                  padding: 1,
                  borderRadius: 1
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 120 }}>
                  {String(column?.columnDef.header)}:
                </Typography>
                <Button
                  size="small"
                  onClick={() => handleRemoveFilter(field)}
                  sx={{ minWidth: 'auto', p: 0.5 }}
                  aria-label={t('common:REMOVE')}
                >
                  ×
                </Button>
                <div className="col-span-2">
                  {filterType === 'text' && (
                    <DebouncedInput
                      value={column?.getFilterValue() as string}
                      type={filterType}
                      onChange={handleTextFilterValueChange(column)}
                    />
                  )}
                  {filterType === 'range' && column?.columnDef.meta?.filterInputType === 'date' && (
                    <div className="flex gap-1 items-center">
                      <DatePicker
                        initialValue={(column?.getFilterValue() as [string, string])?.[0] ?? null}
                        onChange={(isoString) =>
                          handleRangeFilterValueChange(column, false)(isoString)
                        }
                        onClear={() => handleRangeFilterValueChange(column, false)('')}
                        size="small"
                        minDate={new Date(1970, 1, 1)}
                      />
                      <span>-</span>
                      <DatePicker
                        initialValue={(column?.getFilterValue() as [string, string])?.[1] ?? null}
                        onChange={(isoString) =>
                          handleRangeFilterValueChange(column, true)(isoString)
                        }
                        onClear={() => handleRangeFilterValueChange(column, true)('')}
                        size="small"
                        minDate={new Date(1970, 1, 1)}
                      />
                    </div>
                  )}
                  {filterType === 'range' && column?.columnDef.meta?.filterInputType !== 'date' && (
                    <div className="flex gap-1 items-center">
                      <DebouncedInput
                        value={
                          (column?.getFilterValue() as [number | string, number | string])?.[0] ??
                          ''
                        }
                        onChange={handleRangeFilterValueChange(column, false)}
                        type={column?.columnDef.meta?.filterInputType ?? 'number'}
                      />
                      <span>-</span>
                      <DebouncedInput
                        value={
                          (column?.getFilterValue() as [number | string, number | string])?.[1] ??
                          ''
                        }
                        onChange={handleRangeFilterValueChange(column, true)}
                        type={column?.columnDef.meta?.filterInputType ?? 'number'}
                      />
                    </div>
                  )}
                  {filterType === 'select' && (
                    <Select
                      options={
                        column?.columnDef.meta?.filterOptions ??
                        Array.from(column?.getFacetedUniqueValues()?.keys() ?? [])
                          .sort((a, b) => String(a).localeCompare(String(b)))
                          .map((value) => ({
                            value,
                            text: value
                          }))
                      }
                      emptyOption
                      value={(column?.getFilterValue() ?? '') as string}
                      onChange={handleSelectFilterValueChange(column)}
                    />
                  )}
                  {filterType === 'boolean' && (
                    <Select
                      options={[
                        {
                          value: 'false',
                          text: t('common:NO')
                        },
                        {
                          value: 'true',
                          text: t('common:YES')
                        }
                      ]}
                      emptyOption
                      value={
                        column?.getFilterValue() === undefined
                          ? ''
                          : String(column?.getFilterValue())
                      }
                      onChange={handleBooleanFilterValueChange(column)}
                    />
                  )}
                </div>
              </Box>
            );
          })}
        </Stack>
      )}
      {filterColumns.length > 0 && (
        <FormControl size="small">
          <MuiSelect
            value={selectedField}
            onChange={handleFilterChange}
            displayEmpty
            input={<InputBase />}
            renderValue={() => (
              <Button
                startIcon={<PlusIcon />}
                sx={{
                  color: 'primary.main',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'transparent'
                  }
                }}
              >
                {t('common:ADD_FILTER')}
              </Button>
            )}
            sx={{
              '& .MuiSelect-select': {
                padding: '0 !important',
                border: 'none'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              }
            }}
          >
            {filterColumns
              .filter((column) => !table.getState().columnFilters.find((f) => f.id === column.id))
              .map((column) => (
                <MenuItem key={column.id} value={column.id}>
                  {String(column.columnDef.header)}
                </MenuItem>
              ))}
          </MuiSelect>
        </FormControl>
      )}
    </Stack>
  );
};

/**
 * A debounced input component that updates
 * the value only if there hasn't been any changes for 500ms
 */
const DebouncedInput = ({
  value: initialValue,
  onChange,
  type = 'text',
  placeholder
}: {
  value: string | number;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
}) => {
  const [debouncedValue, setDebouncedValue] = useState(initialValue);

  // Update internal state only when initialValue changes
  useEffect(() => {
    setDebouncedValue(initialValue);
  }, [initialValue]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDebouncedValue(e.target.value);
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (debouncedValue !== initialValue) {
        onChange(String(debouncedValue));
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [debouncedValue, onChange, initialValue]);

  return (
    <Input
      type={type}
      value={String(debouncedValue ?? '')}
      onChange={handleInputChange}
      placeholder={placeholder}
    />
  );
};
